import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useNavigation } from '../hooks/useNavigation';
import { useEmpresaPath } from '../hooks/useEmpresaPath';
import { pathWithEmpresa } from '../utils/companyUrl';
import empleadoService from '../services/empleadoService';
import construccionCivilService from '../services/construccionCivilService';
import configuracionPlanillaAlternativaService from '../services/configuracionPlanillaAlternativaService';
import ImportarEmpleados from '../components/ImportarEmpleados';
import AsignarContratoModal from '../components/AsignarContratoModal';
import contratosEmpleadoService from '../services/contratosEmpleadoService';
import configuracionExpedienteLaboralService from '../services/configuracionExpedienteLaboralService';
import configuracionFotosPersonalService from '../services/configuracionFotosPersonalService';
import expedienteLaboralService from '../services/expedienteLaboralService';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ExcelJS from 'exceljs';
import { MOTIVOS_CESE, MOTIVO_CESE_OTROS, motivoCeseEnLista, construirMotivoCese } from '../constants/motivosCese';
import {
  estadoInternoDeEmpleado,
  estadoPlameDeEmpleado,
  empleadoVigenteEnAlgunaPlanilla,
} from '../utils/fechaImportacion';

const obtenerNombreEmpresa = (empresa) =>
  empresa?.name || empresa?.nombre || empresa?.nombre_empresa || 'EMPRESA';

const obtenerDetalleEmpresa = (empresa) => {
  const texto = empresa?.description || empresa?.descripcion || empresa?.direccion || empresa?.address;
  return texto?.trim() || '';
};

/** Con planilla alternativa: Interno = solo_interna (o contratación Interno). */
const esEmpleadoPlanillaInterna = (empleado) => {
  if (!empleado) return false;
  if (String(empleado.tipo_planilla || '').toLowerCase() === 'solo_interna') return true;
  return String(empleado.tipo_contratacion || '').trim().toLowerCase() === 'interno';
};

/** Etiqueta corta de tipo de contratación: Planilla | RH | Interno */
const etiquetaTipoContratacionEmpleado = (empleado) => {
  const tipo = String(empleado?.tipo_contratacion || '').trim();
  if (!tipo) return 'Sin tipo';
  const lower = tipo.toLowerCase();
  if (lower === 'interno') return 'Interno';
  if (lower.includes('honorario') || lower === 'rh') return 'RH';
  if (lower === 'planilla') return 'Planilla';
  return tipo;
};

const claseBadgeTipoContratacion = (empleado) => {
  const etiqueta = etiquetaTipoContratacionEmpleado(empleado);
  if (etiqueta === 'Interno') return 'bg-amber-100 text-amber-800';
  if (etiqueta === 'RH') return 'bg-violet-100 text-violet-800';
  if (etiqueta === 'Planilla') return 'bg-indigo-100 text-indigo-800';
  return 'bg-gray-100 text-gray-700';
};

const claseBadgeEstado = (estado) => {
  if (estado === 'Activo') return 'bg-green-100 text-green-800';
  if (estado === 'Cesado') return 'bg-red-100 text-red-800';
  return 'bg-gray-100 text-gray-800';
};

const esRegimenConstruccionCivilEmpleado = (empleado) => {
  const cod = String(empleado?.regimen_laboral?.codigo || '').toUpperCase();
  if (cod === 'CONSU' || cod === 'CONSTRUCCION') return true;
  const nombre = String(empleado?.regimen_laboral?.nombre || '').toLowerCase();
  return nombre.includes('construcción') || nombre.includes('construccion');
};

const parametroConsuVigente = (parametros, categoria, diaIso = new Date().toISOString().slice(0, 10)) => {
  const cat = String(categoria || 'operario');
  const dia = String(diaIso).slice(0, 10);
  return (parametros || [])
    .filter((p) => {
      if (String(p.categoria) !== cat) return false;
      const desde = String(p.vigente_desde || '').slice(0, 10);
      const hasta = p.vigente_hasta ? String(p.vigente_hasta).slice(0, 10) : '9999-12-31';
      return desde && dia >= desde && dia <= hasta;
    })
    .sort((a, b) => String(b.vigente_desde).localeCompare(String(a.vigente_desde)))[0] || null;
};

const jornalDiarioConsuEmpleado = (empleado, parametrosConsu) => {
  if (!esRegimenConstruccionCivilEmpleado(empleado)) return null;
  const categoria = empleado.construccion?.categoria || 'operario';
  const param = parametroConsuVigente(parametrosConsu, categoria);
  if (!param) return null;
  const jornal = parseFloat(param.jornal_basico);
  return Number.isFinite(jornal) ? jornal : null;
};

const etiquetaSistemaPensionario = (empleado) => {
  const sistema = String(empleado?.sistema_pensionario || '').trim().toUpperCase();
  if (!sistema) {
    return empleado?.afp_id || empleado?.afp ? 'AFP' : '';
  }
  if (sistema === 'ONP') return 'ONP';
  if (sistema === 'AFP') {
    const nombreCrudo = empleado.afp?.nombre || empleado.afp_nombre || '';
    const corto = String(nombreCrudo).replace(/^AFP\s+/i, '').trim();
    const tipo = empleado.tipo_afp ? ` · ${empleado.tipo_afp}` : '';
    return corto ? `AFP ${corto}${tipo}` : `AFP${tipo}`;
  }
  return empleado.sistema_pensionario;
};

const claseBadgePension = (empleado) => {
  const sistema = String(empleado?.sistema_pensionario || '').toUpperCase();
  if (sistema === 'ONP') return 'bg-slate-100 text-slate-800';
  if (sistema === 'AFP' || empleado?.afp_id) return 'bg-orange-100 text-orange-800';
  return 'bg-gray-100 text-gray-600';
};

/** Recibos por honorarios no llevan régimen pensionario en listado. */
const mostrarRegimenPensionarioEnLista = (empleado) =>
  etiquetaTipoContratacionEmpleado(empleado) !== 'RH';

const Empleados = () => {
  const { selectedCompany, empresaIdFromUrl } = useAuth();
  const { contratosActivados } = useNavigation();
  const nuevoEmpleadoPath = useEmpresaPath('/empleados/nuevo');
  const empresaActivaId = selectedCompany?.id || empresaIdFromUrl;
  const [expedienteLaboralActivo, setExpedienteLaboralActivo] = useState(false);
  const [fotosPersonalActivo, setFotosPersonalActivo] = useState(false);
  const [descargandoExpedienteId, setDescargandoExpedienteId] = useState(null);
  
  // Estados principales
  const [empleadosCompletos, setEmpleadosCompletos] = useState([]); // Todos los empleados sin filtrar
  const [empleados, setEmpleados] = useState([]); // Empleados filtrados y paginados
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState({ visible: false, tipo: '', texto: '' });

  // Estados para filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterCargo, setFilterCargo] = useState('');
  const [filterProyecto, setFilterProyecto] = useState('');
  const [filterTipoPlanilla, setFilterTipoPlanilla] = useState('');
  const [filterTipoContratacion, setFilterTipoContratacion] = useState('');
  const [sortField, setSortField] = useState('nombre_completo');
  const [sortDirection, setSortDirection] = useState('asc');

  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmpleados, setTotalEmpleados] = useState(0);
  const [perPage] = useState(10);

  // Estados para sistema de bajas/altas
  const [showBajaModal, setShowBajaModal] = useState(false);
  const [showHistorialModal, setShowHistorialModal] = useState(false);
  const [empleadoParaBaja, setEmpleadoParaBaja] = useState(null);
  const [procesandoBaja, setProcesandoBaja] = useState(false);
  const [fechaBaja, setFechaBaja] = useState('');
  const [motivoBaja, setMotivoBaja] = useState('');
  const [motivoBajaDetalle, setMotivoBajaDetalle] = useState('');
  const [fechaReingreso, setFechaReingreso] = useState('');
  const [historialEditable, setHistorialEditable] = useState([]);
  const [historialEditableInterno, setHistorialEditableInterno] = useState([]);
  const [editandoHistorial, setEditandoHistorial] = useState(null);
  const [editandoHistorialTipo, setEditandoHistorialTipo] = useState('plame'); // plame | interno
  const [showEditarHistorialModal, setShowEditarHistorialModal] = useState(false);
  const [fechaIngresoEdit, setFechaIngresoEdit] = useState('');
  const [fechaCeseEdit, setFechaCeseEdit] = useState('');
  const [motivoCeseEdit, setMotivoCeseEdit] = useState('');

  // Estados para reportes
  const [showReporteModal, setShowReporteModal] = useState(false);
  const [empleadoParaReporte, setEmpleadoParaReporte] = useState(null);
  const [generandoPDF, setGenerandoPDF] = useState(false);

  // Estados para importación
  const [showImportarModal, setShowImportarModal] = useState(false);

  // Planilla alternativa
  const [planillaAlternativaActiva, setPlanillaAlternativaActiva] = useState(false);
  const [acoplandoPlanilla, setAcoplandoPlanilla] = useState(false);

  // Estados para asignación de contratos
  const [showAsignarContratoModal, setShowAsignarContratoModal] = useState(false);
  const [contratoSoloHistorial, setContratoSoloHistorial] = useState(false);
  const [empleadoParaContrato, setEmpleadoParaContrato] = useState(null);
  const [contratosPorEmpleado, setContratosPorEmpleado] = useState({});
  const [contratoARenovar, setContratoARenovar] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [parametrosConsu, setParametrosConsu] = useState([]);

  // Cargar empleados al montar el componente o cuando cambie la empresa
  useEffect(() => {
    if (selectedCompany?.id) {
      cargarTodosLosEmpleados();
    }
  }, [selectedCompany]);

  useEffect(() => {
    const cargarPlanillaAlternativa = async () => {
      if (!selectedCompany?.id) {
        setPlanillaAlternativaActiva(false);
        return;
      }
      try {
        const response = await configuracionPlanillaAlternativaService.verificarEstado(selectedCompany.id);
        setPlanillaAlternativaActiva(Boolean(response?.data?.planilla_alternativa_activa));
      } catch (error) {
        console.error('Error al verificar planilla alternativa:', error);
        setPlanillaAlternativaActiva(false);
      }
    };

    cargarPlanillaAlternativa();

    const onConfigActualizada = () => cargarPlanillaAlternativa();
    window.addEventListener('configuracion-planilla-alternativa-actualizada', onConfigActualizada);
    return () => {
      window.removeEventListener('configuracion-planilla-alternativa-actualizada', onConfigActualizada);
    };
  }, [selectedCompany?.id]);

  useEffect(() => {
    const cargarExpedienteLaboral = async () => {
      if (!selectedCompany?.id) {
        setExpedienteLaboralActivo(false);
        return;
      }
      try {
        const response = await configuracionExpedienteLaboralService.verificarEstado(selectedCompany.id);
        setExpedienteLaboralActivo(
          Boolean(response?.data?.expediente_laboral_activo ?? response?.data?.activo)
        );
      } catch (error) {
        console.error('Error al verificar expediente laboral:', error);
        setExpedienteLaboralActivo(false);
      }
    };

    cargarExpedienteLaboral();

    const onConfigActualizada = () => cargarExpedienteLaboral();
    window.addEventListener('configuracion-expediente-laboral-actualizada', onConfigActualizada);
    return () => {
      window.removeEventListener('configuracion-expediente-laboral-actualizada', onConfigActualizada);
    };
  }, [selectedCompany?.id]);

  useEffect(() => {
    const cargarFotosPersonal = async () => {
      if (!selectedCompany?.id) {
        setFotosPersonalActivo(false);
        return;
      }
      try {
        const response = await configuracionFotosPersonalService.verificarEstado(selectedCompany.id);
        setFotosPersonalActivo(
          Boolean(response?.data?.fotos_personal_activo ?? response?.data?.activo)
        );
      } catch (error) {
        console.error('Error al verificar fotos del personal:', error);
        setFotosPersonalActivo(false);
      }
    };

    cargarFotosPersonal();

    const onConfigActualizada = () => cargarFotosPersonal();
    window.addEventListener('configuracion-fotos-personal-actualizada', onConfigActualizada);
    return () => {
      window.removeEventListener('configuracion-fotos-personal-actualizada', onConfigActualizada);
    };
  }, [selectedCompany?.id]);

  useEffect(() => {
    if (selectedCompany?.id && contratosActivados) {
      cargarResumenContratos();
    } else {
      setContratosPorEmpleado({});
    }
  }, [selectedCompany?.id, contratosActivados]);

  useEffect(() => {
    const cargarParametrosConsu = async () => {
      if (!selectedCompany?.id) {
        setParametrosConsu([]);
        return;
      }
      try {
        const response = await construccionCivilService.parametrosGlobales();
        if (response.success) {
          setParametrosConsu(response.data || []);
        } else {
          setParametrosConsu([]);
        }
      } catch (error) {
        console.error('Error al cargar parámetros CONSU para lista de empleados:', error);
        setParametrosConsu([]);
      }
    };
    cargarParametrosConsu();
  }, [selectedCompany?.id]);

  const cargarResumenContratos = async () => {
    const response = await contratosEmpleadoService.obtenerResumenPorEmpresa(selectedCompany.id);
    if (response.success) {
      setContratosPorEmpleado(response.data || {});
    }
  };

  // Aplicar filtros locales cuando cambien los criterios de búsqueda/filtrado
  useEffect(() => {
    aplicarFiltrosLocales();
  }, [empleadosCompletos, searchTerm, filterEstado, filterCargo, filterProyecto, filterTipoPlanilla, filterTipoContratacion, sortField, sortDirection, currentPage, planillaAlternativaActiva]);

  // Función para cargar TODOS los empleados desde la API (una sola vez)
  const cargarTodosLosEmpleados = async () => {
    try {
      setLoading(true);
      setError('');

      // Cargar todos los empleados sin paginación ni filtros
      const params = {
        per_page: 1000, // Cargar muchos empleados de una vez
        empresa_id: selectedCompany.id
      };

      console.log('🔍 Cargando TODOS los empleados de la empresa:', selectedCompany.name);
      const response = await empleadoService.getAllEmpleados(params);
      
      if (response.success) {
        const todosLosEmpleados = response.data.data || [];
        setEmpleadosCompletos(todosLosEmpleados);
        console.log('✅ Empleados completos cargados:', todosLosEmpleados.length);
        
        // Los filtros se aplicarán automáticamente por el segundo useEffect
      } else {
        setError('Error al cargar empleados');
      }
    } catch (err) {
      console.error('❌ Error cargando empleados:', err);
      setError(err.message || 'Error al cargar empleados');
    } finally {
      setLoading(false);
    }
  };

  // Función para aplicar filtros y búsqueda localmente
  const aplicarFiltrosLocales = () => {
    if (empleadosCompletos.length === 0) return;

    let empleadosFiltrados = [...empleadosCompletos];

    // Aplicar búsqueda por texto
    if (searchTerm && searchTerm.trim() !== '') {
      const termino = searchTerm.toLowerCase().trim();
      empleadosFiltrados = empleadosFiltrados.filter(empleado => {
        return (
          empleado.nombre_completo?.toLowerCase().includes(termino) ||
          empleado.numero_documento?.toLowerCase().includes(termino) ||
          empleado.email?.toLowerCase().includes(termino) ||
          empleado.telefono?.toLowerCase().includes(termino) ||
          empleado.cargo?.toLowerCase().includes(termino) ||
          empleado.categoria?.toLowerCase().includes(termino) ||
          empleado.proyecto?.toLowerCase().includes(termino)
        );
      });
    }

    // Aplicar filtro por estado (con planilla alternativa: Activo si PLAME o interna sigue vigente)
    if (filterEstado && filterEstado.trim() !== '') {
      empleadosFiltrados = empleadosFiltrados.filter((empleado) => {
        if (!planillaAlternativaActiva) {
          return empleado.estado === filterEstado;
        }
        const vigente = empleadoVigenteEnAlgunaPlanilla(empleado);
        if (filterEstado === 'Activo') return vigente;
        if (filterEstado === 'Cesado') return !vigente;
        return empleado.estado === filterEstado;
      });
    }

    // Aplicar filtro por cargo
    if (filterCargo && filterCargo.trim() !== '') {
      empleadosFiltrados = empleadosFiltrados.filter(empleado => 
        empleado.cargo === filterCargo
      );
    }

    // Aplicar filtro por proyecto
    if (filterProyecto && filterProyecto.trim() !== '') {
      empleadosFiltrados = empleadosFiltrados.filter(empleado => 
        empleado.proyecto === filterProyecto
      );
    }

    // Aplicar filtro por tipo de contratación (Planilla / RH / Interno)
    if (filterTipoContratacion && filterTipoContratacion.trim() !== '') {
      empleadosFiltrados = empleadosFiltrados.filter((empleado) => {
        const etiqueta = etiquetaTipoContratacionEmpleado(empleado);
        const raw = String(empleado.tipo_contratacion || '').trim();
        return (
          etiqueta === filterTipoContratacion ||
          raw === filterTipoContratacion ||
          (filterTipoContratacion === 'RH' &&
            (etiqueta === 'RH' || raw.toLowerCase().includes('honorario')))
        );
      });
    }

    // Aplicar filtro por modalidad de planilla alternativa (RH dual / solo interna)
    if (planillaAlternativaActiva && filterTipoPlanilla) {
      empleadosFiltrados = empleadosFiltrados.filter((empleado) => {
        const esInterna = esEmpleadoPlanillaInterna(empleado);
        if (filterTipoPlanilla === 'interno') return esInterna;
        if (filterTipoPlanilla === 'dual') return !esInterna;
        return true;
      });
    }

    // Aplicar ordenamiento
    empleadosFiltrados.sort((a, b) => {
      let valorA = a[sortField] || '';
      let valorB = b[sortField] || '';
      
      // Convertir a string para comparación consistente
      valorA = valorA.toString().toLowerCase();
      valorB = valorB.toString().toLowerCase();
      
      if (sortDirection === 'asc') {
        return valorA.localeCompare(valorB);
      } else {
        return valorB.localeCompare(valorA);
      }
    });

    // Calcular paginación
    const totalFiltrados = empleadosFiltrados.length;
    const totalPaginas = Math.ceil(totalFiltrados / perPage) || 1;
    
    // Si la página actual es mayor al total de páginas, resetear a la primera página
    let paginaActual = currentPage;
    if (currentPage > totalPaginas) {
      paginaActual = 1;
      setCurrentPage(1);
    }
    
    const inicioIndice = (paginaActual - 1) * perPage;
    const finIndice = inicioIndice + perPage;
    const empleadosPaginados = empleadosFiltrados.slice(inicioIndice, finIndice);

    // Actualizar estados
    setEmpleados(empleadosPaginados);
    setTotalEmpleados(totalFiltrados);
    setTotalPages(totalPaginas);

    console.log('🔍 Filtros aplicados:', {
      termino: searchTerm,
      estado: filterEstado,
      cargo: filterCargo,
      proyecto: filterProyecto,
      tipoContratacion: filterTipoContratacion,
      tipoPlanilla: filterTipoPlanilla,
      orden: `${sortField} ${sortDirection}`,
      totalOriginal: empleadosCompletos.length,
      totalFiltrado: totalFiltrados,
      pagina: currentPage,
      totalPaginas,
      empleadosMostrados: empleadosPaginados.length
    });
  };

  // Función para buscar empleados (ahora funciona localmente)
  const buscarEmpleados = (termino) => {
    setSearchTerm(termino);
    setCurrentPage(1); // Resetear a la primera página
    // El filtrado se aplica automáticamente por el useEffect
  };

  // Función para cambiar filtros (ahora funciona localmente)
  const cambiarFiltro = (campo, valor) => {
    if (campo === 'estado') {
      setFilterEstado(valor);
    } else if (campo === 'cargo') {
      setFilterCargo(valor);
    } else if (campo === 'proyecto') {
      setFilterProyecto(valor);
    } else if (campo === 'tipo_contratacion') {
      setFilterTipoContratacion(valor);
    } else if (campo === 'tipo_planilla') {
      setFilterTipoPlanilla(valor);
    }
    setCurrentPage(1); // Resetear a la primera página
    // El filtrado se aplica automáticamente por el useEffect
  };

  // Función para cambiar ordenamiento (ahora funciona localmente)
  const cambiarOrdenamiento = (campo) => {
    if (sortField === campo) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(campo);
      setSortDirection('asc');
    }
    setCurrentPage(1);
    // El ordenamiento se aplica automáticamente por el useEffect
  };

  // Función para mostrar mensaje
  const mostrarMensaje = (tipo, texto) => {
    setMensaje({ visible: true, tipo, texto });
    setTimeout(() => {
      setMensaje({ visible: false, tipo: '', texto: '' });
    }, 5000);
  };

  // Función para abrir modal de baja
  const abrirModalBaja = (empleado) => {
    setEmpleadoParaBaja(empleado);
    setFechaBaja(new Date().toISOString().split('T')[0]); // Fecha actual por defecto
    setMotivoBaja('');
    setMotivoBajaDetalle('');
    setShowBajaModal(true);
  };

  // Función para dar de baja empleado
  const darDeBaja = async () => {
    if (!empleadoParaBaja || !fechaBaja || !motivoBaja) return;

    try {
      setProcesandoBaja(true);

      const motivoFinal = construirMotivoCese(motivoBaja, motivoBajaDetalle);
      
      // Crear registro en historial laboral (solo para baja)
      const historialData = {
        empleado_id: empleadoParaBaja.id,
        fecha_cese: fechaBaja,
        motivo_cese: motivoFinal
        // fecha_ingreso se obtiene automáticamente del historial del empleado en el backend
      };
      
      console.log('🔽 DATOS ENVIADOS PARA DAR DE BAJA:');
      console.log('📋 Empleado:', empleadoParaBaja.nombre_completo, '(ID:', empleadoParaBaja.id, ')');
      console.log('📅 Fecha de baja:', fechaBaja);
      console.log('📝 Motivo:', motivoFinal);
      console.log('🚀 Datos completos enviados al endpoint:', historialData);
      console.log('ℹ️ Nota: La fecha_ingreso se obtiene automáticamente del último historial activo del empleado');
      
      await empleadoService.createHistorialLaboral(historialData);
      
      mostrarMensaje('success', `Empleado "${empleadoParaBaja.nombre_completo}" dado de baja en PLAME. El historial interno no se modifica`);
      
      // Recargar la lista
      await cargarTodosLosEmpleados();
      
      // Cerrar modal
      setShowBajaModal(false);
      setEmpleadoParaBaja(null);
      setFechaBaja('');
      setMotivoBaja('');
      setMotivoBajaDetalle('');
      
    } catch (err) {
      console.error('❌ Error dando de baja empleado:', err);
      mostrarMensaje('error', err.message || 'Error al dar de baja empleado');
    } finally {
      setProcesandoBaja(false);
    }
  };

  // Función para formatear fecha ISO a formato local
  const formatearFechaISO = (fechaISO) => {
    if (!fechaISO) return null;
    // Extraer solo la parte de la fecha (YYYY-MM-DD) sin la hora
    const fechaSolo = fechaISO.split('T')[0];
    const [year, month, day] = fechaSolo.split('-');
    return `${day}/${month}/${year}`;
  };

  // Función para abrir modal de historial
  const formatearListaHistorial = (lista = []) =>
    (lista || []).map((registro) => ({
      ...registro,
      fecha_ingreso_formateada: formatearFechaISO(registro.fecha_ingreso),
      fecha_cese_formateada: registro.fecha_cese ? formatearFechaISO(registro.fecha_cese) : null,
    }));

  const refrescarHistorialModalDesdeEmpleado = (empleadoActualizado) => {
    if (!empleadoActualizado) return;
    setEmpleadoParaBaja(empleadoActualizado);
    setHistorialEditable(formatearListaHistorial(empleadoActualizado.historial_laboral));
    setHistorialEditableInterno(
      formatearListaHistorial(empleadoActualizado.historial_laboral_interno)
    );
  };

  const abrirModalHistorial = (empleado) => {
    setEmpleadoParaBaja(empleado);
    setHistorialEditable(formatearListaHistorial(empleado.historial_laboral));
    setHistorialEditableInterno(formatearListaHistorial(empleado.historial_laboral_interno));
    setShowHistorialModal(true);
  };

  // Función para dar de alta empleado
  const darDeAlta = async () => {
    if (!empleadoParaBaja || !fechaReingreso) return;

    try {
      setProcesandoBaja(true);
      
      // Crear nuevo registro en historial laboral para el reingreso
      const historialData = {
        empleado_id: empleadoParaBaja.id,
        fecha_ingreso: fechaReingreso,
        fecha_cese: null,
        motivo_cese: null
      };
      
      console.log('🔼 DATOS ENVIADOS PARA DAR DE ALTA:');
      console.log('📋 Empleado:', empleadoParaBaja.nombre_completo, '(ID:', empleadoParaBaja.id, ')');
      console.log('📅 Fecha de reingreso:', fechaReingreso);
      console.log('🚀 Datos completos enviados al endpoint:', historialData);
      
      await empleadoService.createHistorialLaboral(historialData);
      
      mostrarMensaje('success', `Empleado "${empleadoParaBaja.nombre_completo}" dado de alta exitosamente`);
      
      // Recargar la lista
      await cargarTodosLosEmpleados();
      
      // Cerrar modal
      setShowHistorialModal(false);
      setEmpleadoParaBaja(null);
      setFechaReingreso('');
      
    } catch (err) {
      console.error('❌ Error dando de alta empleado:', err);
      mostrarMensaje('error', err.message || 'Error al dar de alta empleado');
    } finally {
      setProcesandoBaja(false);
    }
  };

  const abrirModalEditarHistorial = (registro, tipo = 'plame') => {
    if (!registro?.id) {
      mostrarMensaje('error', 'No se pudo identificar el registro a editar');
      return;
    }

    setEditandoHistorialTipo(tipo);
    setEditandoHistorial(registro);
    setFechaIngresoEdit((registro.fecha_ingreso || '').split('T')[0]);
    setFechaCeseEdit((registro.fecha_cese || '').split('T')[0]);
    setMotivoCeseEdit(motivoCeseEnLista(registro.motivo_cese || ''));
    setShowEditarHistorialModal(true);
  };

  const guardarEdicionHistorial = async () => {
    if (!empleadoParaBaja?.id || !editandoHistorial?.id) {
      mostrarMensaje('error', 'No se pudo identificar el registro a actualizar');
      return;
    }

    if (!fechaIngresoEdit) {
      mostrarMensaje('error', 'La fecha de ingreso es obligatoria');
      return;
    }

    if (fechaCeseEdit && !motivoCeseEdit) {
      mostrarMensaje('error', 'Seleccione el motivo de cese');
      return;
    }

    try {
      setProcesandoBaja(true);

      const payload = {
        fecha_ingreso: fechaIngresoEdit,
        fecha_cese: fechaCeseEdit || null,
        motivo_cese: fechaCeseEdit ? (motivoCeseEdit || null) : null
      };

      if (editandoHistorialTipo === 'interno') {
        await empleadoService.updateHistorialLaboralInterno(
          empleadoParaBaja.id,
          editandoHistorial.id,
          payload
        );
      } else {
        await empleadoService.updateHistorialLaboral(
          empleadoParaBaja.id,
          editandoHistorial.id,
          payload
        );
      }

      // Recargar empleados para reflejar cambios de estado Activo/Cesado
      const params = { per_page: 1000, empresa_id: selectedCompany.id };
      const response = await empleadoService.getAllEmpleados(params);
      const empleadosActualizados = response?.data?.data || [];
      setEmpleadosCompletos(empleadosActualizados);

      const empleadoActualizado = empleadosActualizados.find(emp => emp.id === empleadoParaBaja.id);
      refrescarHistorialModalDesdeEmpleado(empleadoActualizado);

      setShowEditarHistorialModal(false);
      setEditandoHistorial(null);
      setEditandoHistorialTipo('plame');
      setFechaIngresoEdit('');
      setFechaCeseEdit('');
      setMotivoCeseEdit('');
      mostrarMensaje('success', 'Registro de historial actualizado correctamente');
    } catch (err) {
      console.error('❌ Error actualizando historial laboral:', err);
      mostrarMensaje('error', err.message || 'Error al actualizar registro del historial');
    } finally {
      setProcesandoBaja(false);
    }
  };

  // Función para eliminar un registro del historial laboral
  const eliminarRegistroHistorial = async (registro, tipo = 'plame') => {
    if (!empleadoParaBaja?.id || !registro?.id) {
      mostrarMensaje('error', 'No se pudo identificar el registro a eliminar');
      return;
    }

    const etiqueta = tipo === 'interno' ? 'historial interno' : 'historial laboral';
    const confirmar = window.confirm(
      `¿Seguro que deseas eliminar este registro del ${etiqueta}? Esta acción no se puede deshacer.`
    );

    if (!confirmar) return;

    try {
      setProcesandoBaja(true);

      if (tipo === 'interno') {
        await empleadoService.deleteHistorialLaboralInterno(empleadoParaBaja.id, registro.id);
      } else {
        await empleadoService.deleteHistorialLaboral(empleadoParaBaja.id, registro.id);
      }

      // Recargar empleados para reflejar el estado correcto (Activo/Cesado) luego de eliminar
      const params = { per_page: 1000, empresa_id: selectedCompany.id };
      const response = await empleadoService.getAllEmpleados(params);
      const empleadosActualizados = response?.data?.data || [];
      setEmpleadosCompletos(empleadosActualizados);

      const empleadoActualizado = empleadosActualizados.find(emp => emp.id === empleadoParaBaja.id);
      if (empleadoActualizado) {
        refrescarHistorialModalDesdeEmpleado(empleadoActualizado);
      } else {
        setShowHistorialModal(false);
        setEmpleadoParaBaja(null);
        setHistorialEditable([]);
        setHistorialEditableInterno([]);
      }

      mostrarMensaje('success', 'Registro de historial eliminado correctamente');
    } catch (err) {
      console.error('❌ Error eliminando registro del historial laboral:', err);
      mostrarMensaje('error', err.message || 'Error al eliminar registro del historial');
    } finally {
      setProcesandoBaja(false);
    }
  };

  // Función para cancelar modal
  const cancelarModal = () => {
    setShowBajaModal(false);
    setShowHistorialModal(false);
    setShowReporteModal(false);
    setEmpleadoParaBaja(null);
    setEmpleadoParaReporte(null);
    setFechaBaja('');
    setMotivoBaja('');
    setMotivoBajaDetalle('');
    setFechaReingreso('');
    setHistorialEditable([]);
    setHistorialEditableInterno([]);
    setEditandoHistorial(null);
    setEditandoHistorialTipo('plame');
    setShowEditarHistorialModal(false);
    setFechaIngresoEdit('');
    setFechaCeseEdit('');
    setMotivoCeseEdit('');
  };

  // Función para abrir modal de reporte
  const abrirModalReporte = (empleado) => {
    setEmpleadoParaReporte(empleado);
    setShowReporteModal(true);
  };

  const descargarExpedienteZip = async (empleado) => {
    if (!empleado?.id) return;
    setDescargandoExpedienteId(empleado.id);
    try {
      const response = await expedienteLaboralService.descargarZip(empleado.id);
      if (response.success) {
        mostrarMensaje('success', `Expediente de ${empleado.nombre_completo} descargado`);
      } else {
        mostrarMensaje('error', response.error || 'No se pudo descargar el expediente');
      }
    } catch (error) {
      console.error('Error al descargar expediente:', error);
      mostrarMensaje('error', 'Error al descargar el expediente');
    } finally {
      setDescargandoExpedienteId(null);
    }
  };

  const abrirModalAsignarContrato = (empleado, soloHistorial = false, renovarDesde = null) => {
    setEmpleadoParaContrato(empleado);
    setContratoSoloHistorial(soloHistorial);
    setContratoARenovar(renovarDesde);
    setShowAsignarContratoModal(true);
  };

  const cerrarModalAsignarContrato = () => {
    setShowAsignarContratoModal(false);
    setEmpleadoParaContrato(null);
    setContratoSoloHistorial(false);
    setContratoARenovar(null);
  };

  // Desde el panel: ?renovarEmpleado=&renovarContrato=
  useEffect(() => {
    const empleadoId = searchParams.get('renovarEmpleado');
    const contratoId = searchParams.get('renovarContrato');
    if (!empleadoId || !contratosActivados) return;
    if (!empleadosCompletos?.length) return;

    const limpiarParamsRenovacion = () => {
      const next = new URLSearchParams(searchParams);
      [
        'renovarEmpleado',
        'renovarContrato',
        'plantilla',
        'plazo',
        'fechaFin',
        'titulo',
        'tipo',
        'lugar',
      ].forEach((k) => next.delete(k));
      setSearchParams(next, { replace: true });
    };

    const empleado = empleadosCompletos.find((e) => String(e.id) === String(empleadoId));
    if (!empleado) {
      limpiarParamsRenovacion();
      return;
    }

    abrirModalAsignarContrato(
      empleado,
      false,
      contratoId
        ? {
            id: Number(contratoId),
            contrato_id: Number(contratoId),
            plantilla_contrato_id: searchParams.get('plantilla')
              ? Number(searchParams.get('plantilla'))
              : undefined,
            plazo_meses: searchParams.get('plazo')
              ? Number(searchParams.get('plazo'))
              : undefined,
            fecha_fin: searchParams.get('fechaFin') || undefined,
            titulo: searchParams.get('titulo') || undefined,
            tipo_contrato: searchParams.get('tipo') || undefined,
            lugar_firma: searchParams.get('lugar') || undefined,
          }
        : null
    );

    limpiarParamsRenovacion();
  }, [empleadosCompletos, searchParams, contratosActivados]);

  // Función para generar PDF del reporte
  const generarReportePDF = async () => {
    if (!empleadoParaReporte) return;

    try {
      setGenerandoPDF(true);
      
      const elemento = document.getElementById('reporte-constancia');
      if (!elemento) {
        throw new Error('No se pudo encontrar el elemento del reporte');
      }

      // Configurar html2canvas para mejor calidad
      const canvas = await html2canvas(elemento, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Calcular dimensiones para ajustar al PDF
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      // Agregar la imagen al PDF
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Si el contenido es más alto que una página, agregar páginas adicionales
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Generar nombre del archivo
      const tipoConstancia = empleadoParaReporte.estado === 'Activo' ? 'Alta' : 'Baja';
      const fechaActual = new Date().toISOString().split('T')[0];
      const nombreArchivo = `Constancia_${tipoConstancia}_${empleadoParaReporte.nombre_completo.replace(/\s+/g, '_')}_${fechaActual}.pdf`;

      // Descargar el PDF
      pdf.save(nombreArchivo);
      
      mostrarMensaje('success', 'Reporte PDF generado exitosamente');
      
    } catch (error) {
      console.error('Error generando PDF:', error);
      mostrarMensaje('error', 'Error al generar el PDF: ' + error.message);
    } finally {
      setGenerandoPDF(false);
    }
  };

  // Función para obtener el último registro de historial laboral
  const obtenerUltimoHistorial = (empleado) => {
    if (!empleado.historial_laboral || empleado.historial_laboral.length === 0) {
      return null;
    }
    
    // Si el empleado está cesado, buscar el registro con fecha_cese más reciente
    if (empleado.estado === 'Cesado') {
      const registrosConCese = empleado.historial_laboral.filter(registro => registro.fecha_cese);
      if (registrosConCese.length > 0) {
        // Ordenar por fecha de cese descendente para obtener el más reciente
        const historialCeseOrdenado = [...registrosConCese].sort((a, b) => 
          new Date(b.fecha_cese) - new Date(a.fecha_cese)
        );
        return historialCeseOrdenado[0];
      }
    }
    
    // Para empleados activos o si no hay registros con cese, ordenar por fecha de ingreso
    const historialOrdenado = [...empleado.historial_laboral].sort((a, b) => 
      new Date(b.fecha_ingreso) - new Date(a.fecha_ingreso)
    );
    
    return historialOrdenado[0];
  };

  const obtenerUltimoHistorialInterno = (empleado) => {
    const historial = empleado.historial_laboral_interno || [];
    if (historial.length === 0) return null;

    return [...historial].sort((a, b) => {
      const fechaA = a.fecha_cese || a.fecha_ingreso;
      const fechaB = b.fecha_cese || b.fecha_ingreso;
      return new Date(fechaB) - new Date(fechaA);
    })[0];
  };

  // Función para formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    
    // Extraer solo la parte de la fecha (YYYY-MM-DD) sin considerar zona horaria
    const fechaStr = fecha.split('T')[0];
    const [year, month, day] = fechaStr.split('-');
    
    // Crear fecha sin ajustes de zona horaria
    return `${day}/${month}/${year}`;
  };

  // Función para formatear salario
  const formatearSalario = (salario) => {
    if (salario === null || salario === undefined || salario === '') return '-';
    const n = parseFloat(salario);
    if (Number.isNaN(n)) return '-';
    return `S/ ${n.toLocaleString('es-PE', { minimumFractionDigits: 2 })}`;
  };

  const renderCeldaRemuneracionLista = (empleado) => {
    const jornal = jornalDiarioConsuEmpleado(empleado, parametrosConsu);
    if (jornal != null) {
      return (
        <div className="space-y-0.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="inline-flex rounded bg-teal-100 px-1 py-0.5 text-[9px] font-semibold text-teal-800">
              JORNAL
            </span>
            <span className="font-medium text-gray-900 tabular-nums">
              {formatearSalario(jornal)}
            </span>
            <span className="text-[10px] text-gray-500">/ día</span>
          </div>
          {empleado.construccion?.categoria && (
            <div className="text-[10px] text-gray-500 capitalize">
              {String(empleado.construccion.categoria).replace(/_/g, ' ')}
            </div>
          )}
        </div>
      );
    }

    if (planillaAlternativaActiva) {
      return (
        <div className="space-y-0.5">
          {!esEmpleadoPlanillaInterna(empleado) && (
            <div className="flex items-center gap-1.5">
              <span className="inline-flex rounded bg-blue-100 px-1 py-0.5 text-[9px] font-semibold text-blue-700">
                PLAME
              </span>
              <span className="font-medium text-gray-900 tabular-nums">
                {formatearSalario(empleado.sueldo_basico)}
              </span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <span className="inline-flex rounded bg-amber-100 px-1 py-0.5 text-[9px] font-semibold text-amber-700">
              INTERNO
            </span>
            <span className="font-medium text-gray-900 tabular-nums">
              {formatearSalario(empleado.sueldo_interno)}
            </span>
          </div>
        </div>
      );
    }

    return (
      <span className="font-medium text-gray-900 tabular-nums">
        {formatearSalario(empleado.sueldo_basico)}
      </span>
    );
  };

  // Obtener listas únicas para filtros (desde todos los empleados, no solo los filtrados)
  const cargosUnicos = [...new Set(empleadosCompletos.map(emp => emp.cargo).filter(Boolean))];
  const proyectosUnicos = [...new Set(empleadosCompletos.map(emp => emp.proyecto).filter(Boolean))];
  const estadosUnicos = ['Activo', 'Cesado'];
  const tiposContratacionUnicos = [...new Set(
    empleadosCompletos
      .map((emp) => etiquetaTipoContratacionEmpleado(emp))
      .filter((t) => t && t !== 'Sin tipo')
  )].sort((a, b) => a.localeCompare(b));

  // Función para obtener iniciales del nombre
  const obtenerIniciales = (nombreCompleto) => {
    if (!nombreCompleto) return '??';
    return nombreCompleto
      .split(' ')
      .slice(0, 2)
      .map(n => n.charAt(0))
      .join('')
      .toUpperCase();
  };

  // Función para abrir modal de importación
  const abrirModalImportacion = () => {
    setShowImportarModal(true);
  };

  const acoplarPersonalPlanillaAlternativa = async () => {
    if (!selectedCompany?.id) return;
    const confirmar = window.confirm(
      'Esto copiará la fecha de ingreso y el sueldo actuales a los campos internos (control) del personal activo que aún no los tenga, y los marcará como Dual. ¿Continuar?'
    );
    if (!confirmar) return;

    try {
      setAcoplandoPlanilla(true);
      const response = await empleadoService.acoplarPlanillaAlternativa(selectedCompany.id);
      mostrarMensaje('success', response.message || 'Personal acoplado correctamente');
      await cargarTodosLosEmpleados();
    } catch (error) {
      mostrarMensaje('error', error.message || 'Error al acoplar personal');
    } finally {
      setAcoplandoPlanilla(false);
    }
  };

  // Función para manejar el éxito de la importación
  const handleImportSuccess = (resultados) => {
    console.log('✅ Importación exitosa:', resultados);
    
    // Mostrar mensaje de éxito
    const empleadosImportados = resultados.exitosos.length;
    const empleadosFallidos = resultados.fallidos.length;
    
    let mensaje = `${empleadosImportados} empleado(s) importado(s) exitosamente`;
    if (empleadosFallidos > 0) {
      mensaje += ` (${empleadosFallidos} fallido(s))`;
    }
    
    mostrarMensaje('success', mensaje);
    
    // Recargar la lista de empleados
    cargarTodosLosEmpleados();
    
    // Cerrar modal
    setShowImportarModal(false);
  };

  /**
   * Columnas del Excel de empleados. Con planilla alternativa activa se separan
   * los datos de la boleta PLAME y los de la boleta interna de control.
   */
  const construirColumnasExportacion = () => {
    const nombreAfpDe = (empleado) =>
      empleado.afp_nombre || empleado.afp?.nombre || empleado.afp || '';

    const columnasBase = [
      { titulo: 'N°', ancho: 6, alineacion: 'center', valor: (e, ctx) => ctx.indice + 1 },
      { titulo: 'Nombre Completo', ancho: 30, valor: (e) => e.nombre_completo || '' },
      { titulo: 'Tipo Documento', ancho: 20, valor: (e) => e.tipo_documento || '' },
      { titulo: 'Número Documento', ancho: 18, valor: (e) => e.numero_documento || '' },
      { titulo: 'Email', ancho: 30, valor: (e) => e.email || '' },
      { titulo: 'Teléfono', ancho: 15, valor: (e) => e.telefono || '' },
      { titulo: 'Cargo', ancho: 20, valor: (e) => e.cargo || '' },
      { titulo: 'Categoría', ancho: 18, valor: (e) => e.categoria || '' },
      { titulo: 'Área de Trabajo', ancho: 20, valor: (e) => e.areas_de_trabajo || '' },
      { titulo: 'Proyecto', ancho: 22, valor: (e) => e.proyecto || '' },
      { titulo: 'Tipo Contratación', ancho: 20, valor: (e) => e.tipo_contratacion || '' },
      {
        titulo: 'Estado',
        ancho: planillaAlternativaActiva ? 28 : 12,
        alineacion: 'center',
        valor: (e) => {
          if (!planillaAlternativaActiva) return e.estado || '';
          const partes = [];
          if (!esEmpleadoPlanillaInterna(e)) {
            partes.push(`PLAME ${estadoPlameDeEmpleado(e) || e.estado || ''}`);
          }
          const estadoInterno = estadoInternoDeEmpleado(e);
          if (estadoInterno) partes.push(`INTERNA ${estadoInterno}`);
          return partes.join(' / ') || e.estado || '';
        }
      },
      {
        titulo: 'Régimen Laboral',
        ancho: 22,
        valor: (e) => e.regimen_laboral_nombre || e.regimen_laboral?.nombre || e.regimen_laboral || ''
      }
    ];

    const columnasSueldo = planillaAlternativaActiva
      ? [
          {
            titulo: 'Modalidad Planilla',
            ancho: 24,
            alineacion: 'center',
            valor: (e) =>
              e.tipo_planilla === 'dual'
                ? 'Dual (PLAME + interna)'
                : e.tipo_planilla === 'solo_interna'
                  ? 'Solo interna'
                  : ''
          },
          {
            titulo: 'Sueldo PLAME',
            ancho: 16,
            tipo: 'moneda',
            alineacion: 'right',
            valor: (e, ctx) => {
              if (ctx.esSoloInterna) return '';
              const jornal = jornalDiarioConsuEmpleado(e, ctx.parametrosConsu || []);
              if (jornal != null) return jornal;
              return parseFloat(e.sueldo_basico) || 0;
            },
          },
          {
            titulo: 'Sueldo Interno (control)',
            ancho: 24,
            tipo: 'moneda',
            alineacion: 'right',
            valor: (e) => parseFloat(e.sueldo_interno) || 0
          }
        ]
      : [
          {
            titulo: 'Sueldo / Jornal',
            ancho: 18,
            tipo: 'moneda',
            alineacion: 'right',
            valor: (e, ctx) => {
              const jornal = jornalDiarioConsuEmpleado(e, ctx.parametrosConsu || []);
              if (jornal != null) return jornal;
              return parseFloat(e.sueldo_basico) || 0;
            }
          }
        ];

    const columnasRemuneracion = [
      {
        titulo: 'Asignación Familiar',
        ancho: 20,
        alineacion: 'center',
        valor: (e) => (e.asignacion_familiar ? 'Sí' : 'No')
      },
      { titulo: 'Tiene Bono', ancho: 14, alineacion: 'center', valor: (e) => (e.tiene_bono ? 'Sí' : 'No') },
      {
        titulo: 'Monto Bono',
        ancho: 14,
        tipo: 'moneda',
        alineacion: 'right',
        valor: (e) => (e.tiene_bono ? parseFloat(e.monto_bono) || 0 : 0)
      },
      {
        titulo: 'Viático Standard (esperado)',
        ancho: 26,
        tipo: 'moneda',
        alineacion: 'right',
        valor: (e) => parseFloat(e.monto_viatico_standard) || 0
      },
      { titulo: 'Hora Entrada', ancho: 14, alineacion: 'center', valor: (e) => e.hora_entrada || '' },
      { titulo: 'Hora Salida', ancho: 14, alineacion: 'center', valor: (e) => e.hora_salida || '' },
      {
        titulo: 'Sistema Pensionario',
        ancho: 20,
        alineacion: 'center',
        valor: (e) => e.sistema_pensionario || e.tipo_aporte || (nombreAfpDe(e) ? 'AFP' : 'ONP')
      },
      { titulo: 'AFP', ancho: 18, valor: (e) => nombreAfpDe(e) },
      {
        titulo: 'Tipo AFP',
        ancho: 14,
        alineacion: 'center',
        valor: (e) => e.tipo_afp || e.afp?.tipo_afp || e.comision_tipo || ''
      },
      { titulo: 'CUSPP', ancho: 18, valor: (e) => e.cuspp || e.codigo_cuspp || e.afp?.cuspp || '' },
      { titulo: 'Banco', ancho: 20, valor: (e) => e.banco || '' },
      { titulo: 'Número Cuenta', ancho: 20, valor: (e) => e.cuenta_bancaria || '' },
      { titulo: 'CCI', ancho: 22, valor: (e) => e.cci || '' },
      { titulo: 'Contacto Emergencia', ancho: 26, valor: (e) => e.contacto_emergencia || '' },
      { titulo: 'Teléfono Emergencia', ancho: 20, valor: (e) => e.telefono_emergencia || '' }
    ];

    const columnasHistorial = planillaAlternativaActiva
      ? [
          {
            titulo: 'Fecha Ingreso PLAME',
            ancho: 20,
            alineacion: 'center',
            valor: (e, ctx) =>
              ctx.esSoloInterna || !ctx.ultimoHistorial?.fecha_ingreso
                ? ''
                : formatearFecha(ctx.ultimoHistorial.fecha_ingreso)
          },
          {
            titulo: 'Fecha Cese PLAME',
            ancho: 18,
            alineacion: 'center',
            valor: (e, ctx) =>
              ctx.esSoloInterna || !ctx.ultimoHistorial?.fecha_cese
                ? ''
                : formatearFecha(ctx.ultimoHistorial.fecha_cese)
          },
          {
            titulo: 'Motivo Cese PLAME',
            ancho: 24,
            valor: (e, ctx) => (ctx.esSoloInterna ? '' : ctx.ultimoHistorial?.motivo_cese || '')
          },
          {
            titulo: 'Fecha Ingreso Interna',
            ancho: 21,
            alineacion: 'center',
            valor: (e, ctx) => {
              const fecha = ctx.ultimoHistorialInterno?.fecha_ingreso || e.fecha_ingreso_interna;
              return fecha ? formatearFecha(fecha) : '';
            }
          },
          {
            titulo: 'Fecha Cese Interna',
            ancho: 19,
            alineacion: 'center',
            valor: (e, ctx) =>
              ctx.ultimoHistorialInterno?.fecha_cese
                ? formatearFecha(ctx.ultimoHistorialInterno.fecha_cese)
                : ''
          },
          {
            titulo: 'Motivo Cese Interna',
            ancho: 24,
            valor: (e, ctx) => ctx.ultimoHistorialInterno?.motivo_cese || ''
          }
        ]
      : [
          {
            titulo: 'Fecha Ingreso',
            ancho: 16,
            alineacion: 'center',
            valor: (e, ctx) =>
              ctx.ultimoHistorial?.fecha_ingreso ? formatearFecha(ctx.ultimoHistorial.fecha_ingreso) : ''
          },
          {
            titulo: 'Fecha Cese',
            ancho: 16,
            alineacion: 'center',
            valor: (e, ctx) =>
              ctx.ultimoHistorial?.fecha_cese ? formatearFecha(ctx.ultimoHistorial.fecha_cese) : ''
          },
          { titulo: 'Motivo Cese', ancho: 24, valor: (e, ctx) => ctx.ultimoHistorial?.motivo_cese || '' }
        ];

    const columnasPersonales = [
      {
        titulo: 'Fecha Nacimiento',
        ancho: 18,
        alineacion: 'center',
        valor: (e) => (e.fecha_nacimiento ? formatearFecha(e.fecha_nacimiento) : '')
      },
      { titulo: 'Sexo', ancho: 12, alineacion: 'center', valor: (e) => e.sexo || '' },
      { titulo: 'Estado Civil', ancho: 15, alineacion: 'center', valor: (e) => e.estado_civil || '' },
      { titulo: 'Número Hijos', ancho: 14, alineacion: 'center', valor: (e) => e.numero_hijos || 0 },
      { titulo: 'Dirección', ancho: 35, valor: (e) => e.direccion || '' },
      { titulo: 'Distrito', ancho: 18, valor: (e) => e.distrito || '' },
      { titulo: 'Provincia', ancho: 18, valor: (e) => e.provincia || '' },
      { titulo: 'Departamento', ancho: 18, valor: (e) => e.departamento || '' }
    ];

    return [
      ...columnasBase,
      ...columnasSueldo,
      ...columnasRemuneracion,
      ...columnasHistorial,
      ...columnasPersonales
    ];
  };

  // Función para exportar empleados a Excel
  const exportarEmpleadosExcel = async () => {
    try {
      mostrarMensaje('success', 'Preparando descarga del archivo Excel...');
      
      // Obtener todos los empleados sin paginación para la exportación
      const params = {
        empresa_id: selectedCompany.id,
        search: searchTerm || undefined,
        estado: filterEstado || undefined,
        cargo: filterCargo || undefined,
        proyecto: filterProyecto || undefined,
        sort_field: sortField,
        sort_direction: sortDirection,
        per_page: 1000 // Obtener muchos registros para exportar
      };

      console.log('📋 Exportando empleados con parámetros:', params);
      const response = await empleadoService.getAllEmpleados(params);
      
      if (!response.success || !response.data.data) {
        throw new Error('No se pudieron obtener los datos de empleados');
      }

      const empleadosParaExportar = response.data.data;
      console.log(`📊 Exportando ${empleadosParaExportar.length} empleados`);

      const columnas = construirColumnasExportacion();

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Sistema de Planillas';
      workbook.created = new Date();
      const worksheet = workbook.addWorksheet('Empleados', {
        properties: { defaultRowHeight: 18 },
        views: [{ state: 'frozen', ySplit: 4 }]
      });

      const totalColumnas = columnas.length;
      const bordeFino = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };

      const titleRow = worksheet.addRow(['Empleados']);
      titleRow.height = 24;
      worksheet.mergeCells(1, 1, 1, totalColumnas);
      const titleCell = titleRow.getCell(1);
      titleCell.font = { bold: true, size: 14 };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };

      const fechaGeneracion = new Date().toLocaleDateString('es-PE');
      const subtitleRow = worksheet.addRow([
        `${selectedCompany?.name || 'Empresa'}  |  Total de empleados: ${empleadosParaExportar.length}  |  Fecha de generación: ${fechaGeneracion}`
      ]);
      worksheet.mergeCells(2, 1, 2, totalColumnas);
      subtitleRow.getCell(1).font = { size: 11, italic: true };
      subtitleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

      const filtrosAplicados = [
        searchTerm ? `Búsqueda: ${searchTerm}` : null,
        filterEstado ? `Estado: ${filterEstado}` : null,
        filterCargo ? `Cargo: ${filterCargo}` : null,
        filterProyecto ? `Proyecto: ${filterProyecto}` : null,
        planillaAlternativaActiva ? 'Planilla alternativa activa (PLAME + interna)' : null
      ].filter(Boolean);
      const filtrosRow = worksheet.addRow([
        filtrosAplicados.length > 0 ? filtrosAplicados.join('  |  ') : 'Sin filtros aplicados'
      ]);
      worksheet.mergeCells(3, 1, 3, totalColumnas);
      filtrosRow.getCell(1).font = { size: 10, italic: true, color: { argb: 'FF6B7280' } };
      filtrosRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

      const headerRow = worksheet.addRow(columnas.map((columna) => columna.titulo));
      headerRow.height = 26;
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7C3AED' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = bordeFino;
      });

      columnas.forEach((columna, indice) => {
        worksheet.getColumn(indice + 1).width = columna.ancho;
      });

      empleadosParaExportar.forEach((empleado, index) => {
        const contexto = {
          indice: index,
          ultimoHistorial: obtenerUltimoHistorial(empleado),
          ultimoHistorialInterno: obtenerUltimoHistorialInterno(empleado),
          esSoloInterna: empleado.tipo_planilla === 'solo_interna',
          parametrosConsu,
        };

        const row = worksheet.addRow(columnas.map((columna) => columna.valor(empleado, contexto)));
        row.eachCell((cell, colNumber) => {
          const columna = columnas[colNumber - 1];
          cell.border = bordeFino;
          cell.alignment = {
            vertical: 'middle',
            wrapText: true,
            horizontal: columna.alineacion || 'left'
          };
          if (columna.tipo === 'moneda') {
            cell.numFmt = '"S/"#,##0.00';
          }
          if (index % 2 === 1) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };
          }
        });
      });

      worksheet.autoFilter = {
        from: { row: headerRow.number, column: 1 },
        to: { row: headerRow.number + empleadosParaExportar.length, column: totalColumnas }
      };

      worksheet.addRow([]);
      const pieRow = worksheet.addRow([
        `Total de empleados: ${empleadosParaExportar.length}  |  Generado por: Sistema de Planillas - ${selectedCompany?.name || 'Empresa'}`
      ]);
      worksheet.mergeCells(pieRow.number, 1, pieRow.number, totalColumnas);
      pieRow.getCell(1).font = { size: 10, italic: true };
      pieRow.getCell(1).alignment = { horizontal: 'center' };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fechaActual = new Date().toISOString().split('T')[0];
      const nombreEmpresa = (selectedCompany.name || 'Empresa').replace(/[^a-zA-Z0-9]/g, '_');
      const nombreArchivo = `Empleados_${nombreEmpresa}_${fechaActual}.xlsx`;
      link.download = nombreArchivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`✅ Archivo Excel generado: ${nombreArchivo}`);
      mostrarMensaje('success', `Archivo Excel descargado: ${nombreArchivo}`);

    } catch (error) {
      console.error('❌ Error exportando a Excel:', error);
      mostrarMensaje('error', 'Error al generar el archivo Excel: ' + error.message);
    }
  };

  // Si no hay empresa seleccionada
  if (!selectedCompany) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay empresa seleccionada</h3>
          <p className="text-gray-500 text-center">
            Selecciona una empresa para ver la lista de empleados.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-5">
      {/* Header */}
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-bold text-gray-900">Empleados</h1>
          <p className="mt-2 text-sm text-gray-700">
            Lista de empleados de {selectedCompany.name} ({totalEmpleados} empleados)
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <div className="flex flex-wrap gap-3 justify-end">
            {planillaAlternativaActiva && (
              <button
                onClick={acoplarPersonalPlanillaAlternativa}
                disabled={acoplandoPlanilla}
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60 sm:w-auto"
                title="Copia fecha/sueldo actuales a campos internos del personal existente"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                {acoplandoPlanilla ? 'Acoplando...' : 'Acoplar personal'}
              </button>
            )}
            <button
              onClick={exportarEmpleadosExcel}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 sm:w-auto"
              title="Exportar empleados a Excel"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Exportar
            </button>
            <button
              onClick={abrirModalImportacion}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:w-auto"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              Importar
            </button>
            <Link
              to={nuevoEmpleadoPath}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Agregar Empleado
            </Link>
          </div>
        </div>
      </div>

      {/* Mensajes */}
      {mensaje.visible && (
        <div className={`mb-6 p-4 rounded-md ${
          mensaje.tipo === 'success' ? 'bg-green-100 text-green-700 border border-green-300' : 
          'bg-red-100 text-red-700 border border-red-300'
        }`}>
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mensaje.tipo === 'success' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              )}
            </svg>
            {mensaje.texto}
          </div>
        </div>
      )}

      {/* Filtros y búsqueda */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${planillaAlternativaActiva ? 'lg:grid-cols-7' : 'lg:grid-cols-6'}`}>
          {/* Búsqueda */}
        <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Buscar empleado
          </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
          <input
            type="text"
            id="search"
            value={searchTerm}
                onChange={(e) => buscarEmpleados(e.target.value)}
                placeholder="Buscar por nombre, documento, email..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
          </div>

          {/* Filtro por estado */}
          <div>
            <label htmlFor="estado" className="block text-sm font-medium text-gray-700 mb-1">
              Estado
            </label>
            <select
              id="estado"
              value={filterEstado}
              onChange={(e) => cambiarFiltro('estado', e.target.value)}
              className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">Todos los estados</option>
              {estadosUnicos.map(estado => (
                <option key={estado} value={estado}>{estado}</option>
              ))}
            </select>
          </div>

          {/* Filtro por tipo de contratación: Planilla / RH */}
          <div>
            <label htmlFor="tipo_contratacion" className="block text-sm font-medium text-gray-700 mb-1">
              Contratación
            </label>
            <select
              id="tipo_contratacion"
              value={filterTipoContratacion}
              onChange={(e) => cambiarFiltro('tipo_contratacion', e.target.value)}
              className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">Todos</option>
              {tiposContratacionUnicos.map((tipo) => (
                <option key={tipo} value={tipo}>
                  {tipo === 'RH' ? 'RH (Recibo por Honorarios)' : tipo}
                </option>
              ))}
            </select>
          </div>

          {/* Filtro por cargo */}
        <div>
            <label htmlFor="cargo" className="block text-sm font-medium text-gray-700 mb-1">
              Cargo
          </label>
          <select
              id="cargo"
              value={filterCargo}
              onChange={(e) => cambiarFiltro('cargo', e.target.value)}
              className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">Todos los cargos</option>
              {cargosUnicos.map(cargo => (
                <option key={cargo} value={cargo}>{cargo}</option>
            ))}
          </select>
          </div>

          {/* Filtro por proyecto */}
          <div>
            <label htmlFor="proyecto" className="block text-sm font-medium text-gray-700 mb-1">
              Proyecto
            </label>
            <select
              id="proyecto"
              value={filterProyecto}
              onChange={(e) => cambiarFiltro('proyecto', e.target.value)}
              className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">Todos los proyectos</option>
              {proyectosUnicos.map(proyecto => (
                <option key={proyecto} value={proyecto}>{proyecto}</option>
              ))}
            </select>
          </div>

          {planillaAlternativaActiva && (
            <div>
              <label htmlFor="tipo_planilla" className="block text-sm font-medium text-gray-700 mb-1">
                Modalidad
              </label>
              <select
                id="tipo_planilla"
                value={filterTipoPlanilla}
                onChange={(e) => cambiarFiltro('tipo_planilla', e.target.value)}
                className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">Todas</option>
                <option value="dual">Dual (PLAME + interna)</option>
                <option value="interno">Solo interna</option>
              </select>
            </div>
          )}

          {/* Botón limpiar filtros */}
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterEstado('');
                setFilterCargo('');
                setFilterProyecto('');
                setFilterTipoContratacion('');
                setFilterTipoPlanilla('');
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Limpiar filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabla de empleados */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Cargando empleados...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-64">
            <div className="text-red-400 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error al cargar empleados</h3>
            <p className="text-gray-500 text-center mb-4">{error}</p>
            <button
              onClick={cargarTodosLosEmpleados}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Reintentar
            </button>
          </div>
        ) : empleados.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <div className="text-gray-400 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay empleados</h3>
            <p className="text-gray-500 text-center mb-4">
              {searchTerm || filterEstado || filterCargo || filterProyecto || filterTipoContratacion || filterTipoPlanilla
                ? 'No se encontraron empleados que coincidan con los filtros.' 
                : 'Aún no hay empleados registrados en esta empresa.'
              }
            </p>
            {!searchTerm && !filterEstado && !filterCargo && !filterProyecto && !filterTipoContratacion && !filterTipoPlanilla && (
              <Link
                to={nuevoEmpleadoPath}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Agregar primer empleado
              </Link>
            )}
          </div>
        ) : (
          <>
        <div className="w-full overflow-hidden">
          <table className="w-full table-fixed divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                    <th 
                      className="w-[28%] px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                      onClick={() => cambiarOrdenamiento('nombre_completo')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Empleado</span>
                        {sortField === 'nombre_completo' && (
                          <svg className={`w-3 h-3 ${sortDirection === 'asc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                </th>
                    <th 
                      className="w-[20%] px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                      onClick={() => cambiarOrdenamiento('cargo')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Cargo</span>
                        {sortField === 'cargo' && (
                          <svg className={`w-3 h-3 ${sortDirection === 'asc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                </th>
                <th className={`${planillaAlternativaActiva ? 'w-[11%]' : 'w-[10%]'} px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wide`}>
                  Tipo
                </th>
                    <th 
                      className="w-[16%] px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                      onClick={() => cambiarOrdenamiento('sueldo_basico')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>{planillaAlternativaActiva ? 'Sueldos' : 'Sueldo / Jornal'}</span>
                        {sortField === 'sueldo_basico' && (
                          <svg className={`w-3 h-3 ${sortDirection === 'asc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                </th>
                <th className="w-[9%] px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wide">
                  Ingreso
                </th>
                    <th 
                      className="w-[8%] px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                      onClick={() => cambiarOrdenamiento('estado')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Estado</span>
                        {sortField === 'estado' && (
                          <svg className={`w-3 h-3 ${sortDirection === 'asc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                </th>
                <th className="w-[8%] px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wide">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
                  {empleados.map((empleado) => (
                <tr key={empleado.id} className="hover:bg-gray-50">
                  <td className="px-2 py-2 align-top">
                    <div className="flex items-start min-w-0">
                      <div className="flex-shrink-0 h-7 w-7 mt-0.5">
                            {fotosPersonalActivo && empleado.foto_carnet_url ? (
                              <img
                                src={empleadoService.urlFotoCarnet(empleado.foto_carnet_url)}
                                alt={empleado.nombre_completo}
                                className="h-7 w-7 rounded-full object-cover"
                              />
                            ) : (
                            <div className="h-7 w-7 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center">
                              <span className="text-[10px] font-medium text-white">
                                {obtenerIniciales(empleado.nombre_completo)}
                          </span>
                        </div>
                            )}
                      </div>
                      <div className="ml-2 min-w-0">
                        <div className="text-xs font-medium text-gray-900 truncate" title={empleado.nombre_completo}>
                              {empleado.nombre_completo}
                        </div>
                            <div className="text-[11px] text-gray-500 truncate" title={`${empleado.tipo_documento}: ${empleado.numero_documento}`}>
                              {empleado.numero_documento}
                            </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2 align-top min-w-0">
                        <div className="text-xs text-gray-900 font-medium truncate" title={empleado.cargo}>
                          {empleado.cargo || '—'}
                        </div>
                        <div className="text-[11px] text-gray-500 truncate" title={[empleado.categoria, empleado.proyecto].filter(Boolean).join(' · ')}>
                          {[empleado.categoria, empleado.proyecto].filter(Boolean).join(' · ') || '—'}
                        </div>
                  </td>
                  <td className="px-2 py-2 align-top">
                    <div className="flex flex-col gap-1 items-start">
                      <span
                        className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${claseBadgeTipoContratacion(empleado)}`}
                        title={empleado.tipo_contratacion || 'Sin tipo de contratación'}
                      >
                        {etiquetaTipoContratacionEmpleado(empleado)}
                      </span>
                      {planillaAlternativaActiva &&
                        etiquetaTipoContratacionEmpleado(empleado) !== 'Interno' && (
                        <span
                          className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${
                            esEmpleadoPlanillaInterna(empleado)
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-sky-100 text-sky-800'
                          }`}
                        >
                          {esEmpleadoPlanillaInterna(empleado) ? 'Interna' : 'Dual'}
                        </span>
                      )}
                      {mostrarRegimenPensionarioEnLista(empleado) &&
                        etiquetaSistemaPensionario(empleado) && (
                        <span
                          className={`inline-flex max-w-full px-1.5 py-0.5 text-[10px] font-semibold rounded-full truncate ${claseBadgePension(empleado)}`}
                          title={etiquetaSistemaPensionario(empleado)}
                        >
                          {etiquetaSistemaPensionario(empleado)}
                        </span>
                      )}
                    </div>
                  </td>
                      <td className="px-2 py-2 align-top text-xs">
                        {renderCeldaRemuneracionLista(empleado)}
                  </td>
                  <td className="px-2 py-2 align-top text-xs text-gray-900 whitespace-nowrap">
                        {empleado.historial_laboral?.[0]?.fecha_ingreso 
                          ? formatearFecha(empleado.historial_laboral[0].fecha_ingreso)
                          : '-'
                        }
                  </td>
                  <td className="px-2 py-2 align-top">
                    {planillaAlternativaActiva ? (
                      <div className="flex flex-col gap-0.5 items-start">
                        {!esEmpleadoPlanillaInterna(empleado) && (
                          <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${claseBadgeEstado(estadoPlameDeEmpleado(empleado) || empleado.estado)}`}>
                            PLAME {estadoPlameDeEmpleado(empleado) || empleado.estado}
                          </span>
                        )}
                        {estadoInternoDeEmpleado(empleado) && (
                          <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${claseBadgeEstado(estadoInternoDeEmpleado(empleado))}`}>
                            INTERNA {estadoInternoDeEmpleado(empleado)}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${claseBadgeEstado(empleado.estado)}`}>
                        {empleado.estado}
                      </span>
                    )}
                  </td>
                      <td className="px-2 py-2 align-top text-[11px] font-medium leading-tight">
                        <div className="flex flex-col items-start gap-0.5">
                    <Link
                      to={pathWithEmpresa(`/empleados/${empleado.id}/editar`, empresaActivaId)}
                            className="text-yellow-700 hover:text-yellow-900"
                    >
                            Editar
                    </Link>
                          <button
                            type="button"
                            onClick={() => abrirModalReporte(empleado)}
                            className="text-purple-700 hover:text-purple-900"
                          >
                            Reporte
                          </button>

                          {expedienteLaboralActivo && (
                            <button
                              type="button"
                              onClick={() => descargarExpedienteZip(empleado)}
                              disabled={descargandoExpedienteId === empleado.id}
                              className="text-slate-700 hover:text-slate-900 disabled:opacity-50"
                            >
                              {descargandoExpedienteId === empleado.id ? 'ZIP...' : 'Expediente'}
                            </button>
                          )}

                          {contratosActivados && empleado.estado === 'Activo' && (
                            <button
                              type="button"
                              onClick={() => abrirModalAsignarContrato(empleado, false)}
                              className="text-indigo-700 hover:text-indigo-900"
                            >
                              {contratosPorEmpleado[empleado.id]
                                ? `Contratos(${contratosPorEmpleado[empleado.id]})`
                                : 'Contrato'}
                            </button>
                          )}

                          {contratosActivados && empleado.estado === 'Cesado' && (
                            <button
                              type="button"
                              onClick={() => abrirModalAsignarContrato(empleado, true)}
                              className="text-indigo-700 hover:text-indigo-900"
                            >
                              Contratos
                              {contratosPorEmpleado[empleado.id]
                                ? `(${contratosPorEmpleado[empleado.id]})`
                                : ''}
                            </button>
                          )}
                          
                          {!esEmpleadoPlanillaInterna(empleado) && estadoPlameDeEmpleado(empleado) === 'Activo' && (
                            <button
                              type="button"
                              onClick={() => abrirModalBaja(empleado)}
                              className="text-orange-700 hover:text-orange-900"
                            >
                              Baja
                            </button>
                          )}
                          {(estadoPlameDeEmpleado(empleado) === 'Cesado'
                            || planillaAlternativaActiva
                            || esEmpleadoPlanillaInterna(empleado)) && (
                            <button
                              type="button"
                              onClick={() => abrirModalHistorial(empleado)}
                              className="text-green-700 hover:text-green-900"
                            >
                              Historial
                            </button>
                          )}
                        </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{((currentPage - 1) * perPage) + 1}</span> a{' '}
                      <span className="font-medium">{Math.min(currentPage * perPage, totalEmpleados)}</span> de{' '}
                      <span className="font-medium">{totalEmpleados}</span> empleados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Anterior</span>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      
                      {/* Números de página */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              pageNum === currentPage
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Siguiente</span>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de dar de baja */}
      {showBajaModal && empleadoParaBaja && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100">
                <svg className="h-6 w-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4 text-center">Dar de Baja Empleado</h3>
              <div className="mt-4">
                <p className="text-sm text-gray-600 text-center mb-4">
                  <span className="font-medium text-gray-900">{empleadoParaBaja.nombre_completo}</span>
                </p>
                {planillaAlternativaActiva && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">
                    Esta baja aplica solo a PLAME. El trabajador seguirá activo en la planilla interna.
                  </p>
                )}
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de Baja *
                    </label>
                    <input
                      type="date"
                      value={fechaBaja}
                      onChange={(e) => setFechaBaja(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Motivo de Baja *
                    </label>
                    <select
                      value={motivoBaja}
                      onChange={(e) => {
                        setMotivoBaja(e.target.value);
                        if (e.target.value !== MOTIVO_CESE_OTROS) {
                          setMotivoBajaDetalle('');
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    >
                      <option value="">Seleccionar motivo</option>
                      {MOTIVOS_CESE.map((motivo) => (
                        <option key={motivo} value={motivo}>
                          {motivo}
                        </option>
                      ))}
                    </select>
                  </div>

                  {motivoBaja === MOTIVO_CESE_OTROS && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Especificar motivo <span className="text-gray-400 font-normal">(opcional)</span>
                      </label>
                      <input
                        type="text"
                        value={motivoBajaDetalle}
                        onChange={(e) => setMotivoBajaDetalle(e.target.value)}
                        placeholder="Ej. Mutuo acuerdo, traslado de sede..."
                        maxLength={255}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-center space-x-4 mt-6">
                <button
                  onClick={cancelarModal}
                  disabled={procesandoBaja}
                  className="px-4 py-2 bg-white text-gray-500 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={darDeBaja}
                  disabled={procesandoBaja || !fechaBaja || !motivoBaja}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md text-sm font-medium hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 flex items-center"
                >
                  {procesandoBaja ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Procesando...
                    </>
                  ) : (
                    'Dar de Baja'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de historial laboral */}
      {showHistorialModal && empleadoParaBaja && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Historial Laboral - {empleadoParaBaja.nombre_completo}
                </h3>
                <button
                  onClick={cancelarModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Historial PLAME */}
              <div className={`mb-6 overflow-hidden rounded-lg border ${planillaAlternativaActiva ? 'border-slate-200' : 'border-gray-200'}`}>
                {planillaAlternativaActiva && (
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-900">Historial Laboral PLAME</h4>
                    <p className="text-xs text-slate-600">Vínculo real / declarado (oficial)</p>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {planillaAlternativaActiva ? 'Fecha Ingreso (real / PLAME)' : 'Fecha Ingreso'}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fecha Cese
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Motivo Cese
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acción
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {historialEditable.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-6 text-center text-sm text-gray-500">
                            Sin registros de historial PLAME.
                          </td>
                        </tr>
                      ) : (
                        historialEditable.map((registro, index) => (
                          <tr key={registro.id || `plame-${index}`} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {registro.fecha_ingreso_formateada || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {registro.fecha_cese_formateada || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {registro.motivo_cese || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                !registro.fecha_cese
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {!registro.fecha_cese ? 'Activo' : 'Cesado'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => abrirModalEditarHistorial(registro, 'plame')}
                                  disabled={procesandoBaja || !registro?.id}
                                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Editar registro"
                                >
                                  Editar
                                </button>
                                {index === 0 ? (
                                  <button
                                    onClick={() => eliminarRegistroHistorial(registro, 'plame')}
                                    disabled={procesandoBaja || !registro?.id}
                                    className="px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Eliminar registro"
                                  >
                                    Borrar
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Historial Interno */}
              {planillaAlternativaActiva && (
                <div className="mb-6 overflow-hidden rounded-lg border border-amber-200">
                  <div className="bg-amber-50 px-4 py-3 border-b border-amber-200">
                    <h4 className="text-sm font-semibold text-amber-950">Historial Laboral Interno</h4>
                    <p className="text-xs text-amber-800">Vínculo de control (planilla interna). Independiente del cese PLAME.</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-amber-50/60">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-amber-900 uppercase tracking-wider">
                            Fecha Ingreso
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-amber-900 uppercase tracking-wider">
                            Fecha Cese
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-amber-900 uppercase tracking-wider">
                            Motivo Cese
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-amber-900 uppercase tracking-wider">
                            Estado
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-amber-900 uppercase tracking-wider">
                            Acción
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {historialEditableInterno.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-6 text-center text-sm text-gray-500">
                              Sin registros de historial interno.
                            </td>
                          </tr>
                        ) : (
                          historialEditableInterno.map((registro, index) => (
                            <tr key={registro.id || `interno-${index}`} className="hover:bg-amber-50/40">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {registro.fecha_ingreso_formateada || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {registro.fecha_cese_formateada || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {registro.motivo_cese || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  !registro.fecha_cese
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {!registro.fecha_cese ? 'Activo' : 'Cesado'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => abrirModalEditarHistorial(registro, 'interno')}
                                    disabled={procesandoBaja || !registro?.id}
                                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Editar registro interno"
                                  >
                                    Editar
                                  </button>
                                  {index === 0 ? (
                                    <button
                                      onClick={() => eliminarRegistroHistorial(registro, 'interno')}
                                      disabled={procesandoBaja || !registro?.id}
                                      className="px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="Eliminar registro interno"
                                    >
                                      Borrar
                                    </button>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Sección para dar de alta */}
              {empleadoParaBaja.estado !== 'Activo' && (
                <div className="bg-green-50 p-4 rounded-md">
                  <h4 className="text-md font-medium text-green-800 mb-3">Dar de Alta</h4>
                  <div className="flex items-end space-x-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-green-700 mb-1">
                        Fecha de Reingreso *
                      </label>
                      <input
                        type="date"
                        value={fechaReingreso}
                        onChange={(e) => setFechaReingreso(e.target.value)}
                        className="w-full px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        required
                      />
                    </div>
                    <button
                      onClick={darDeAlta}
                      disabled={procesandoBaja || !fechaReingreso}
                      className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 flex items-center"
                    >
                      {procesandoBaja ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Procesando...
                        </>
                      ) : (
                        'Dar de Alta'
                      )}
                    </button>
                  </div>
                </div>
              )}
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={cancelarModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edición de historial laboral */}
      {showEditarHistorialModal && editandoHistorial && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Editar Registro de Historial {editandoHistorialTipo === 'interno' ? 'Interno' : (planillaAlternativaActiva ? 'PLAME' : 'Laboral')}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {editandoHistorialTipo === 'interno'
                      ? 'Fecha Ingreso *'
                      : (planillaAlternativaActiva ? 'Fecha Ingreso (real / PLAME) *' : 'Fecha Ingreso *')}
                  </label>
                  <input
                    type="date"
                    value={fechaIngresoEdit}
                    onChange={(e) => setFechaIngresoEdit(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha Cese (opcional)
                  </label>
                  <input
                    type="date"
                    value={fechaCeseEdit}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFechaCeseEdit(val);
                      if (!val) setMotivoCeseEdit('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Si dejas fecha de cese en blanco, el empleado quedará activo.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Motivo Cese {fechaCeseEdit ? '*' : '(opcional)'}
                  </label>
                  <select
                    value={motivoCeseEdit}
                    onChange={(e) => setMotivoCeseEdit(e.target.value)}
                    disabled={!fechaCeseEdit}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    <option value="">Seleccionar motivo</option>
                    {MOTIVOS_CESE.map((motivo) => (
                      <option key={motivo} value={motivo}>
                        {motivo}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowEditarHistorialModal(false);
                    setEditandoHistorial(null);
                    setFechaIngresoEdit('');
                    setFechaCeseEdit('');
                    setMotivoCeseEdit('');
                  }}
                  disabled={procesandoBaja}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={guardarEdicionHistorial}
                  disabled={procesandoBaja || !fechaIngresoEdit || (fechaCeseEdit && !motivoCeseEdit)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  {procesandoBaja ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de reporte/constancia */}
      {showReporteModal && empleadoParaReporte && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Constancia de {empleadoParaReporte.estado === 'Activo' ? 'Alta' : 'Baja'} Laboral
                </h3>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={generarReportePDF}
                    disabled={generandoPDF}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 flex items-center"
                  >
                    {generandoPDF ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generando PDF...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Exportar PDF
                      </>
                    )}
                  </button>
                  <button
                    onClick={cancelarModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Contenido de la constancia */}
              <div id="reporte-constancia" className="bg-white p-8 border-2 border-gray-200 rounded-lg">
                {/* Encabezado de la empresa */}
                <div className="text-center mb-8">
                  <h1 className="text-2xl font-bold text-gray-800 mb-2">
                    {obtenerNombreEmpresa(selectedCompany)}
                  </h1>
                  <p className="text-gray-600">
                    RUC: {selectedCompany?.ruc || 'N/A'}
                  </p>
                  {obtenerDetalleEmpresa(selectedCompany) && (
                    <p className="text-gray-600">
                      {obtenerDetalleEmpresa(selectedCompany)}
                    </p>
                  )}
                </div>

                {/* Título de la constancia */}
                <div className="text-center mb-8">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">
                    CONSTANCIA DE {empleadoParaReporte.estado === 'Activo' ? 'ALTA' : 'BAJA'} LABORAL
                  </h2>
                  <div className="w-24 h-1 bg-purple-600 mx-auto"></div>
                </div>

                {/* Contenido principal */}
                <div className="space-y-6 text-justify leading-relaxed">
                  <p className="text-gray-700">
                    Por medio de la presente, se deja constancia que el(la) Sr(a). <strong>{empleadoParaReporte.nombre_completo}</strong>, 
                    identificado(a) con {empleadoParaReporte.tipo_documento}: <strong>{empleadoParaReporte.numero_documento}</strong>, 
                    {empleadoParaReporte.estado === 'Activo' ? ' se encuentra laborando' : ' cesó en sus funciones'} en nuestra empresa.
                  </p>

                  {/* Información del empleado */}
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-4">DATOS DEL EMPLEADO:</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Nombre completo:</span>
                        <p className="text-gray-600">{empleadoParaReporte.nombre_completo}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Documento:</span>
                        <p className="text-gray-600">{empleadoParaReporte.tipo_documento}: {empleadoParaReporte.numero_documento}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Cargo:</span>
                        <p className="text-gray-600">{empleadoParaReporte.cargo}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Categoría:</span>
                        <p className="text-gray-600">{empleadoParaReporte.categoria}</p>
                      </div>
                      {empleadoParaReporte.proyecto && (
                        <div>
                          <span className="font-medium text-gray-700">Proyecto:</span>
                          <p className="text-gray-600">{empleadoParaReporte.proyecto}</p>
                        </div>
                      )}
                      <div>
                        <span className="font-medium text-gray-700">Estado actual:</span>
                        <p className="text-gray-600">{empleadoParaReporte.estado}</p>
                      </div>
                    </div>
                  </div>

                  {/* Información del historial laboral */}
                  {(() => {
                    const ultimoHistorial = obtenerUltimoHistorial(empleadoParaReporte);
                    if (ultimoHistorial) {
                      return (
                        <div className="bg-blue-50 p-6 rounded-lg">
                          <h3 className="font-semibold text-gray-800 mb-4">INFORMACIÓN LABORAL:</h3>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-gray-700">Fecha de ingreso:</span>
                              <p className="text-gray-600">{formatearFechaISO(ultimoHistorial.fecha_ingreso)}</p>
                            </div>
                            {ultimoHistorial.fecha_cese && (
                              <>
                                <div>
                                  <span className="font-medium text-gray-700">Fecha de cese:</span>
                                  <p className="text-gray-600">{formatearFechaISO(ultimoHistorial.fecha_cese)}</p>
                                </div>
                                <div className="col-span-2">
                                  <span className="font-medium text-gray-700">Motivo de cese:</span>
                                  <p className="text-gray-600">{ultimoHistorial.motivo_cese}</p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Párrafo de cierre */}
                  <p className="text-gray-700">
                    Se expide la presente constancia a solicitud del interesado(a) para los fines que estime conveniente.
                  </p>
                </div>

                {/* Pie de página */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="flex justify-between items-end">
                    <div className="text-left">
                      <p className="text-sm text-gray-600">
                        Fecha de emisión: {new Date().toLocaleDateString('es-PE', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="border-t border-gray-400 w-48 mb-2"></div>
                      <p className="text-sm font-medium text-gray-700">
                        Recursos Humanos
                      </p>
                      <p className="text-xs text-gray-600">
                        {obtenerNombreEmpresa(selectedCompany)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={cancelarModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de importación */}
      {showImportarModal && (
        <ImportarEmpleados
          onClose={() => setShowImportarModal(false)}
          onImportSuccess={handleImportSuccess}
        />
      )}

      {showAsignarContratoModal && empleadoParaContrato && (
        <AsignarContratoModal
          isOpen={showAsignarContratoModal}
          onClose={cerrarModalAsignarContrato}
          empleado={empleadoParaContrato}
          empresa={selectedCompany}
          empresaId={selectedCompany?.id}
          soloHistorial={contratoSoloHistorial}
          contratoARenovar={contratoARenovar}
          onRenovacionAplicada={() => setContratoARenovar(null)}
          onSuccess={(mensaje) => {
            mostrarMensaje('success', mensaje || 'Operación completada');
            cargarResumenContratos();
          }}
        />
      )}
    </div>
  );
};

export default Empleados; 